import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * PhoneButton — Tap-to-call / click-to-reveal phone number
 *
 * Behaviour:
 *   • Mobile (touch device or narrow viewport):  Opens tel: link → native dialler
 *   • Desktop (hover device + wide viewport):    Toggles inline phone number display
 *
 * Props:
 *   phoneNumber — Raw 10-digit mobile string (e.g. "9876543210")
 *   isDark      — Theme flag
 *   size        — Icon size: 'sm' | 'md' (default 'sm')
 * ═══════════════════════════════════════════════════════════════════════
 */

const SIZE_MAP = {
  sm: { icon: 14, btn: '1.375rem', font: '0.625rem' },
  md: { icon: 16, btn: '1.625rem', font: '0.6875rem' },
};

export default function PhoneButton({ phoneNumber, isDark = false, size = 'sm' }) {
  const [showNumber, setShowNumber] = useState(false);
  const wrapperRef = useRef(null);
  const dims = SIZE_MAP[size] || SIZE_MAP.sm;

  // Is this a touch / mobile device?
  const isMobile = typeof window !== 'undefined' && (
    'ontouchstart' in window || navigator.maxTouchPoints > 0 || window.innerWidth < 768
  );

  // Format phone for display: "98765 43210"
  const formatted = phoneNumber
    ? phoneNumber.replace(/(\d{5})(\d{5})/, '$1 $2')
    : '';

  // E.164 tel link — assume Indian +91 if 10 digits
  const telHref = phoneNumber
    ? `tel:${phoneNumber.length === 10 ? '+91' : ''}${phoneNumber}`
    : '#';

  // Close on outside click
  const handleClickOutside = useCallback((e) => {
    if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
      setShowNumber(false);
    }
  }, []);

  useEffect(() => {
    if (showNumber) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNumber, handleClickOutside]);

  if (!phoneNumber) return null;

  const handleClick = (e) => {
    e.stopPropagation();
    if (isMobile) {
      // Let the <a> tag navigate to tel:
      return;
    }
    // Desktop: toggle inline number
    setShowNumber((prev) => !prev);
  };

  const iconColor = isDark ? '#60a5fa' : '#3b82f6';
  const bgIdle = isDark ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.06)';

  const buttonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
    height: dims.btn,
    minWidth: dims.btn,
    padding: showNumber ? '0 0.5rem' : '0',
    borderRadius: '0.375rem',
    border: 'none',
    cursor: 'pointer',
    background: bgIdle,
    color: iconColor,
    transition: 'background 0.15s, padding 0.2s',
    flexShrink: 0,
    textDecoration: 'none',
    lineHeight: 1,
  };

  const innerContent = (
    <>
      <PhoneIcon size={dims.icon} />
      {showNumber && !isMobile && (
        <span
          style={{
            fontSize: dims.font,
            fontWeight: 600,
            fontFamily: 'monospace',
            color: isDark ? '#93c5fd' : '#2563eb',
            whiteSpace: 'nowrap',
            letterSpacing: '0.02em',
          }}
        >
          {formatted}
        </span>
      )}
    </>
  );

  if (isMobile) {
    return (
      <a
        ref={wrapperRef}
        href={telHref}
        onClick={(e) => e.stopPropagation()}
        style={buttonStyle}
        className="phone-btn"
        title={`Call ${formatted}`}
        aria-label={`Call patient at ${formatted}`}
      >
        {innerContent}
      </a>
    );
  }

  return (
    <button
      ref={wrapperRef}
      type="button"
      onClick={handleClick}
      style={buttonStyle}
      className="phone-btn"
      title={showNumber ? formatted : 'Show phone number'}
      aria-label={showNumber ? `Phone: ${formatted}` : 'Show phone number'}
    >
      {innerContent}
    </button>
  );
}


/* Inline SVG phone icon — no external dependencies */
function PhoneIcon({ size = 14 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}
